import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { avatarFill, avatarInitials, googlePhotoUrl, TIER_LABELS } from 'lib/community/model'
import type { CommunityTier } from 'lib/community/types'
import styles from './community.module.css'

type AvatarSize = 'xs' | 'sm' | 'md' | 'xl'

/** The drawn diameter of each size in CSS px (community.module.css `.avatar_*`; xl at its lg
 *  width) — the photo is asked for at twice this, for sharp discs on 2× screens. */
const AVATAR_PX: Record<AvatarSize, number> = { xs: 18, sm: 28, md: 36, xl: 170 }

interface TierAvatarProps {
  handle: string
  /** null = not a member (an owner who has not signed in): a neutral ring, no tier. */
  tier: CommunityTier | null
  size?: AvatarSize
  className?: string
  /** The member's photo (`avatar_url`) — from the SHARED public payload (identity ticket §3.3),
   *  so the HTML stays identical for every visitor. Absent/null → the initials disc alone. */
  src?: string | null
  /** The member's shown name (`display_name`): the initials come from it when present. */
  name?: string | null
}

/**
 * A photo laid over an initials disc: a plain `<img alt="">` (not `next/image` — a hotlinked
 * Google URL at a known size), no referrer, lazy. A photo that fails hides itself and the disc
 * underneath shows — no broken-image icon, no layout shift (the disc keeps its size; the photo
 * only fills it). A 403 can land before hydration attaches `onError`, so the mount effect also
 * checks for an image that finished without pixels. Keyed on `src` by its users, so a new photo
 * starts unfailed.
 */
export const AvatarPhoto: React.VFC<{ src: string; px: number; className?: string }> = ({
  src,
  px,
  className,
}) => {
  const ref = useRef<HTMLImageElement>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const img = ref.current
    // `currentSrc !== ''`: a lazy image still deferred offscreen has requested nothing yet —
    // only a fetch that actually finished without pixels counts as failed.
    if (img && img.complete && img.currentSrc !== '' && img.naturalWidth === 0) {
      setFailed(true)
    }
  }, [])
  if (failed) {
    return null
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={googlePhotoUrl(src, px * 2)}
      alt=""
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      className={clsx(styles.avatarPhoto, className)}
      onError={() => setFailed(true)}
    />
  )
}

/**
 * A member's disc with the tier as its ring (design 4d: "the tier shows as the avatar ring
 * everywhere"). The fill is derived from the handle (stable whatever the name does); the initials
 * from the shown name when the member shows one, else the handle. With `src` the member's photo
 * is drawn over the disc (Directive 40064d0a: "add an optional src prop … keep the initials as
 * the fallback"). Constraint 40064d0a still holds: the photo is the member's PUBLIC `avatar_url`
 * from the shared-cached payload, never the reader's own picture from AuthContext — the cached
 * HTML is visitor-identical. The placeholder handle `'you'` — a reader who is not a member yet —
 * stays a plain tinted disc.
 */
const TierAvatar: React.VFC<TierAvatarProps> = ({
  handle,
  tier,
  size = 'sm',
  className,
  src = null,
  name = null,
}) => {
  const placeholder = handle.toLowerCase() === 'you' && !name
  const initials = placeholder ? null : avatarInitials(handle, name)
  return (
    <span
      className={clsx(
        styles.avatar,
        styles[`avatar_${size}`],
        styles[`ring_${tier ?? 'none'}`],
        className
      )}
      style={{ backgroundColor: avatarFill(handle) }}
      role="img"
      aria-label={tier ? `${TIER_LABELS[tier]} tier` : 'Owner'}
    >
      {initials}
      {src && <AvatarPhoto key={src} src={src} px={AVATAR_PX[size]} />}
    </span>
  )
}

export default TierAvatar
